import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import useForm from '@hooks/useForm'
import { ClientService } from '@services/index'
import Capabilities from '@utils/capabilities'
import { GACategory } from '@utils/google-analytics'
import GoogleTagManager from '@utils/gtm/google-tag-manager'
import Storage from '@utils/storage'
import * as SignUpValidator from '@utils/validators/signup.validator'

type NewAccountFormData = {
  email?: string
  age?: number
  username?: string
  password?: string
  terms?: boolean
  captchaToken?: string | null
}

const useCreateAccountForm = (purchaseQuantity: number, titleId: string) => {
  const { t } = useTranslation()
  const location = useLocation()

  const { data, updateData, errors, setError, clearError } = useForm<NewAccountFormData>({})
  const [submitting, setSubmitting] = useState(false)

  // region Validation
  const validateTerms = (terms?: boolean): terms is boolean => {
    if (!terms) {
      setError({ terms: t('errors.mustAcceptTerms') })
      return false
    }
    clearError('terms')
    return true
  }

  const validateCaptcha = (captchaToken?: string | null): captchaToken is string => {
    if (!captchaToken) {
      setError({ captchaToken: 'errors.incompleteRecaptcha' })
      return false
    }
    clearError('captchaToken')
    return true
  }

  const validateEmail = async (email?: string): Promise<boolean> => {
    clearError('email')

    const { isValid, errorMsg } = await SignUpValidator.validateEmail(email)
    if (errorMsg) {
      setError({ email: errorMsg })
    }
    return isValid
  }

  const validateUsername = async (username?: string): Promise<boolean> => {
    clearError('username')

    const { isValid, errorMsg } = await SignUpValidator.validateUsername(username)
    if (errorMsg) {
      setError({ username: errorMsg })
    }
    return isValid
  }

  const validatePassword = (password?: string): password is string => {
    clearError('password')

    const { isValid, errorMsg } = SignUpValidator.validatePassword(password)
    if (errorMsg) {
      setError({ password: errorMsg })
    }
    return isValid
  }

  const validateAge = (age?: number): age is number => {
    clearError('age')

    const { isValid, errorMsg } = SignUpValidator.validateAge(age)
    if (errorMsg) {
      setError({ age: errorMsg })
    }
    return isValid
  }
  // endregion Validation

  // region Sign Up Functions
  const signUpWithGoogle = (): boolean => {
    if (!validateTerms(data.terms) || !Capabilities.cookiesEnabled()) {
      return false
    }

    // Used to open and set quantity when redirecting back from Google
    Storage.setPurchasingQuantity(titleId, purchaseQuantity)

    // Track GA event
    GoogleTagManager.trackLegacyEvent(
      GACategory.CONVERSION,
      'signup_click_google',
      location.pathname
    )

    // Navigate to Google Auth page
    window.location.href = `/auth/google/?external_sign_up=true&return_to=${window.location}`
    return true
  }

  const signUpWithFacebook = (): boolean => {
    if (!validateTerms(data.terms) || !Capabilities.cookiesEnabled()) {
      return false
    }

    // Used to open and set quantity when redirecting back from Facebook
    Storage.setPurchasingQuantity(titleId, purchaseQuantity)

    // Track GA event
    GoogleTagManager.trackLegacyEvent(
      GACategory.CONVERSION,
      'signup_click_facebook',
      location.pathname
    )

    // Navigate to Facebook Auth page
    window.location.href = `/auth/facebook/?external_sign_up=true&return_to=${window.location}`
    return true
  }

  const signUp = async (): Promise<void> => {
    setSubmitting(true)

    if (
      (await validateEmail(data.email)) &&
      validateAge(data.age) &&
      (await validateUsername(data.username)) &&
      validatePassword(data.password) &&
      validateCaptcha(data.captchaToken) &&
      validateTerms(data.terms)
    ) {
      // Used to open and set quantity when redirecting back
      Storage.setPurchasingQuantity(titleId, purchaseQuantity)

      const params = {
        email: data.email as string, // Typecast because async functions cannot use type guards
        username: data.username as string, // Typecast because async functions cannot use type guards
        password: data.password,
        password_confirmation: data.password,
        captcha_response: data.captchaToken,
        metadata: {
          age: data.age,
        },
      }

      const resp = await ClientService.registerUser(params)
      if (resp) {
        GoogleTagManager.trackLegacyEvent(GACategory.CONVERSION, 'signup_email', location.pathname)

        const [resp] = await ClientService.loginUser(params.username, params.password)
        if (resp?.data) {
          window.location.reload()
        }
      }
    }

    setSubmitting(false)
  }
  // endregion Sign Up Functions

  return {
    signUp,
    signUpWithFacebook,
    signUpWithGoogle,
    submitting,
    data,
    updateData,
    errors,
    clearError,
  }
}

export default useCreateAccountForm
